import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "New Website",
  "Website Redesign",
  "AI Automation",
  "Local SEO / Google Rankings",
  "Growth Strategy",
  "Not sure yet",
];

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Request sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", business: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at 504-202-1317.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-success"
        className="flex flex-col items-center justify-center rounded-2xl border border-black/10 bg-paper p-12 text-center"
      >
        <div className="grid h-16 w-16 place-items-center rounded-full bg-brand text-white">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="mt-6 font-display text-3xl font-bold tracking-tight">Thank you!</h3>
        <p className="mt-3 max-w-sm text-ink/60">
          Your request is in. Walker will personally reach out within 24 hours to
          talk about growing your business.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="contact-reset"
          className="mt-6 text-sm font-bold text-brand hover:underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Full name *</Label>
          <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Walker Bazile" className="h-12 bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="504-202-1317" className="h-12 bg-white" />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="you@business.com" className="h-12 bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="business">Business name</Label>
          <Input id="business" data-testid="contact-business" value={form.business} onChange={update("business")} placeholder="Your business" className="h-12 bg-white" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>What can we help with?</Label>
        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
          <SelectTrigger data-testid="contact-service" className="h-12 bg-white">
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="message">Tell us about your business</Label>
        <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="What are you hoping to accomplish?" rows={4} className="resize-none bg-white" />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white disabled:opacity-70 sm:w-auto"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Request My Free Quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </>
        )}
      </button>
    </form>
  );
};
